import React, { useState, useEffect } from 'react';
import { Bell, X, Info, AlertTriangle, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../utils/api';

interface Notice {
  id: string;
  title: string;
  content: string;
  type: 'INFO' | 'WARNING' | 'SUCCESS' | 'ERROR';
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  createdBy: string;
  createdAt: string;
  expiresAt?: string;
  isRead: boolean;
  isDismissed: boolean;
  readAt?: string;
  dismissedAt?: string;
}

const NoticeDisplay: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotices, setShowNotices] = useState(false);

  useEffect(() => {
    fetchNotices();
    fetchUnreadCount();
    // Poll for new notices every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotices = async () => {
    try {
      const response = await api.get('/notices');
      const activeNotices = response.data.filter((notice: Notice) => !notice.isDismissed);
      setNotices(activeNotices);
      // Auto-show if there are urgent unread notices
      const hasUrgentUnread = activeNotices.some(
        (n: Notice) => n.priority === 'URGENT' && !n.isRead && !n.isDismissed
      );
      if (hasUrgentUnread) {
        setShowNotices(true);
      }
    } catch (error) {
      console.error('Failed to fetch notices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/notices/unread-count');
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const markAsRead = async (noticeId: string) => {
    try {
      await api.post(`/notices/${noticeId}/read`);
      setNotices(prev => prev.map(notice =>
        notice.id === noticeId
          ? { ...notice, isRead: true, readAt: new Date().toISOString() }
          : notice
      ));
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark notice as read:', error);
    }
  };

  const dismissNotice = async (noticeId: string) => {
    try {
      await api.post(`/notices/${noticeId}/dismiss`);
      setNotices(prev => prev.filter(notice => notice.id !== noticeId));
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to dismiss notice:', error);
    }
  };

  const getNoticeIcon = (type: Notice['type']) => {
    switch (type) {
      case 'INFO': return <Info className="w-5 h-5" />;
      case 'WARNING': return <AlertTriangle className="w-5 h-5" />;
      case 'SUCCESS': return <CheckCircle className="w-5 h-5" />;
      case 'ERROR': return <AlertCircle className="w-5 h-5" />;
      default: return <Info className="w-5 h-5" />;
    }
  };

  const getNoticeStyle = (type: Notice['type']) => {
    switch (type) {
      case 'INFO': return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'WARNING': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'SUCCESS': return 'bg-green-50 border-green-200 text-green-800';
      case 'ERROR': return 'bg-red-50 border-red-200 text-red-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getPriorityStyle = (priority: Notice['priority']) => {
    switch (priority) {
      case 'URGENT': return 'bg-red-100 text-red-700 animate-pulse';
      case 'HIGH': return 'bg-orange-100 text-orange-700';
      case 'NORMAL': return 'bg-blue-100 text-blue-700';
      case 'LOW': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  if (loading) return null;

  return (
    <>
      {/* Notice Bell Icon */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setShowNotices(!showNotices)}
          className="relative bg-primary-600 text-white p-3 rounded-full shadow-lg hover:bg-primary-700 transition-colors"
        >
          <Bell className="w-6 h-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Notice Panel */}
      {showNotices && (
        <div className="fixed bottom-20 right-6 z-50 w-96 max-h-[600px] bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-primary-600 text-white p-4 flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Bell className="w-5 h-5" />
              <h3 className="font-semibold">Notices</h3>
              {unreadCount > 0 && (
                <span className="bg-white text-primary-600 text-xs font-bold px-2 py-0.5 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            <button
              onClick={() => setShowNotices(false)}
              className="text-white hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Notices List */}
          <div className="max-h-[500px] overflow-y-auto">
            {notices.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No notices at this time</p>
              </div>
            ) : (
              <div className="p-2 space-y-2">
                {notices.map((notice) => (
                  <div
                    key={notice.id}
                    className={`border rounded-lg p-3 ${getNoticeStyle(notice.type)} ${
                      !notice.isRead ? 'border-l-4' : ''
                    } transition-all hover:shadow-md`}
                    onMouseEnter={() => !notice.isRead && markAsRead(notice.id)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="mt-1">{getNoticeIcon(notice.type)}</div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className="font-semibold text-gray-900">{notice.title}</h4>
                              {notice.priority !== 'NORMAL' && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityStyle(notice.priority)}`}>
                                  {notice.priority}
                                </span>
                              )}
                              {!notice.isRead && (
                                <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                                  NEW
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{notice.content}</p>
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-500">
                                {formatDate(notice.createdAt)}
                              </p>
                              <button
                                onClick={() => dismissNotice(notice.id)}
                                className="text-xs text-gray-500 hover:text-gray-700"
                              >
                                Dismiss
                              </button>
                            </div>
                          </div>
                          <button
                            onClick={() => dismissNotice(notice.id)}
                            className="ml-2 text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NoticeDisplay;